import * as React from 'react'

import {
  AGGREGATION_STANDARD,
  AGGREGATION_STUDENT,
  ENTITY_LABEL,
  ResultRow,
  getRowMetrics,
  getSortableEntityName,
} from './helpers'

import {
  buildMetricHeaders,
  buildMetricRowCells,
  buildNameHeader,
} from '../../adminMomentumReports/performanceBreakdown'
import { NoPracticeDataEmptyState, } from '../../shared'
import { DataTable, DataTableChip, ShrinkWrapChip, Tooltip, helpIcon, } from '../../../Shared/index'
import useScaledTableColumnWidths from '../../../Shared/hooks/useScaledTableColumnWidths'
import { useStickyTableHeader } from '../../../Shared/hooks/useStickyTableHeader'

const helpIconImg = <img alt={helpIcon.alt} src={helpIcon.src} />

interface PerformanceBreakdownViewProps {
  rows: ResultRow[];
  aggregation: string;
  goal: number;
  isSpecificStudent: boolean;
  // Group By Standard with "All grades": only practiced standards are enumerable
  // (unpracticed ones need a grade). Drives both the footer note and the
  // no-practice-data empty state.
  practicedStandardsOnly: boolean;
  onSelectSkillCategory: (skillCategoryName: string) => void;
  onSelectStudent: (studentId: number | string, studentName: string) => void;
}

const PerformanceBreakdownView: React.FC<PerformanceBreakdownViewProps> = ({ rows, aggregation, goal, isSpecificStudent, practicedStandardsOnly, onSelectSkillCategory, onSelectStudent, }) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const tableRef = React.useRef<HTMLTableElement>(null)

  const isStudent = aggregation === AGGREGATION_STUDENT
  const isStandard = aggregation === AGGREGATION_STANDARD
  const individualMetrics = isStudent || isSpecificStudent
  const labels = ENTITY_LABEL[aggregation]

  const columnWidths = React.useMemo(() => {
    const widths = [
      { width: 300, minWidth: 180 }, // Name (standard/grade/teacher/student)
    ]
    if (isStandard) widths.push({ width: 200, minWidth: 120 }) // Skill Category
    widths.push(
      { width: 200, minWidth: 120 }, // Smart Group
      { width: 200, minWidth: 110 }, // (Avg) Momentum Score
      { width: 200, minWidth: 120 }, // (Avg) Momentum Score Trend
      { width: 175, minWidth: 100 }, // (Avg) Exposure
      { width: 175, minWidth: 100 }, // (Avg) Questions Answered
      { width: 175, minWidth: 100 }, // (Avg) Replay Rate
      { width: 175, minWidth: 100 }, // (Avg) Time Spent
      { width: 200, minWidth: 110 }, // Number of Students Practiced / Last Practiced
    )
    return widths
  }, [isStandard])

  const scaledWidths = useScaledTableColumnWidths({
    columnWidths,
    containerRef,
    maxTableWidth: 2000,
    minTableWidth: 1200,
    additionalWidthRestrictions: 3,
  })

  useStickyTableHeader({
    wrapperRef,
    tableRef,
    enabled: true,
    dependencies: [rows, aggregation],
  })

  const handleSkillCategoryClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { categoryName } = event.currentTarget.dataset
    if (categoryName) onSelectSkillCategory(categoryName)
  }, [onSelectSkillCategory])

  const handleStudentClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { rowId, rowName } = event.currentTarget.dataset
    if (rowId && rowName) onSelectStudent(rowId, rowName)
  }, [onSelectStudent])

  const widths = {
    name: scaledWidths['300w-180min'],
    smartGroup: scaledWidths['200w-120min'],
    momentumScore: scaledWidths['200w-110min'],
    trend: scaledWidths['200w-120min'],
    exposure: scaledWidths['175w-100min'],
    questionsAnswered: scaledWidths['175w-100min'],
    replayRate: scaledWidths['175w-100min'],
    timeSpent: scaledWidths['175w-100min'],
    finalColumn: scaledWidths['200w-110min'],
  }

  const nameHeader = buildNameHeader({ widths, entityLabel: labels })

  const skillCategoryHeader = {
    width: scaledWidths['200w-120min'],
    ariaLabel: 'Skill Category',
    name: (
      <span className="text-and-tooltip-wrapper">
        <span>Skill Category</span>
        <Tooltip
          skipPositioning={true}
          tooltipText="The skill categories this standard aligns to. Select one to open the Skills Report refined to that category."
          tooltipTriggerText={helpIconImg}
        />
      </span>
    ),
    attribute: 'skillCategory',
    sortAttribute: 'skillCategoryForSort',
    isSortable: true,
  }

  const metricHeaders = buildMetricHeaders({
    widths,
    entityLabel: labels,
    goal,
    individualMetrics,
    aggregateTooltips: {
      momentumScore: `The average Momentum Score across the students who practiced this ${labels.singular}.`,
      trend: `The average performance trajectory across the students who practiced this ${labels.singular}.`,
      exposure: `The average amount of practice across the students who practiced this ${labels.singular}. Higher average exposure increases the reliability of the average Momentum Score.`,
      questionsAnswered: `The average number of questions answered per student who practiced this ${labels.singular}.`,
      replayRate: `The average frequency with which students revisit questions in this ${labels.singular}.`,
      timeSpent: `The average time students spent answering questions in this ${labels.singular}.`,
      numberOfStudents: `The number of students who have answered at least one question for this ${labels.singular}.`,
    },
  })

  const headers = isStandard ? [nameHeader, skillCategoryHeader, ...metricHeaders] : [nameHeader, ...metricHeaders]

  const tableRows = React.useMemo(() => rows.map((row) => {
    const { aggregate_id, name, code, description, skills, } = row
    const skillCategories = (skills || []).map((skill) => skill.name)
    const firstCategory = skillCategories[0] || ''

    let nameCell: React.ReactNode = name
    if (isStandard) {
      nameCell = (
        <div className="standard-name-cell">
          <b className="standard-code">{code}</b>
          <div className="standard-description">{description}</div>
        </div>
      )
    } else if (isStudent) {
      nameCell = (
        <button
          className="interactive-wrapper focus-on-light"
          data-row-id={aggregate_id}
          data-row-name={name}
          onClick={handleStudentClick}
          type="button"
        >
          <DataTableChip color="green" label={name} />
        </button>
      )
    }

    return {
      id: aggregate_id,
      name: nameCell,
      skillCategory: isStandard ? (
        <div className="skill-category-cell">
          {skillCategories.map((category) => (
            <ShrinkWrapChip key={category}>
              <button
                className="interactive-wrapper focus-on-light"
                data-category-name={category}
                onClick={handleSkillCategoryClick}
                type="button"
              >
                <DataTableChip color="green" label={category} />
              </button>
            </ShrinkWrapChip>
          ))}
        </div>
      ) : null,
      skillCategoryForSort: firstCategory.toLowerCase(),
      ...buildMetricRowCells(row, goal, getSortableEntityName(aggregation, name)),
    }
  }), [rows, aggregation, goal, isStandard, isStudent, handleSkillCategoryClick, handleStudentClick])

  // With only practiced standards enumerable, an all-unpracticed result set means an
  // empty table; show the same empty state as Smart Groups instead. Kept below every
  // hook: an early return above them would change the hook count between renders
  // once practice data arrives.
  if (practicedStandardsOnly && !rows.some((row) => getRowMetrics(row).questionsAnswered > 0)) {
    return <NoPracticeDataEmptyState />
  }

  return (
    <div className="admin-skills-performance-breakdown admin-standards-performance-breakdown" ref={containerRef}>
      <div className="data-table-wrapper" ref={wrapperRef}>
        <DataTable
          defaultSortAttribute="smartGroupForSort"
          headers={headers}
          reportingFormat={true}
          rows={tableRows}
          showCheckboxes={false}
          tableRef={tableRef}
        />
      </div>
      {practicedStandardsOnly && (
        <section className="unpracticed-section-wrapper" style={{ maxWidth: `${headers.reduce((sum, h) => sum + parseFloat(h.width), 0)}px` }}>
          <div className="unpracticed-section">
            <h2>Showing practiced standards from all grades.</h2>
            <p>Select a specific Standard Grade Level above to view all standards for that grade, including unpracticed ones.</p>
          </div>
        </section>
      )}
    </div>
  )
}

export default PerformanceBreakdownView
